<template>
  <div v-if="loaded">
    <v-toolbar>
      <v-spacer />
      <v-btn
        color="success"
        @click="onSaveDisplaySettings"
        :disabled="!isWriteAvailable"
      >
        Save display settings
        <v-icon dark right>save</v-icon>
      </v-btn>
    </v-toolbar>
    <!-- MAIN VIDEO -->
    <v-card class="main-video-section">
      <v-container grid-list-md>
        <v-layout column>
          <v-flex>
            <h2>Main video (above all videos tabs)</h2>
            <div v-if="mainVideo">
              <h3 class="main-video-section__selected-title">Selected video:</h3>
              <a :href="mainVideo.url" target="_blank">{{ getTextFromHTMLStr(mainVideo.title) }}</a>
            </div>
            <v-select
              :items="visibleVideos"
              :value="displaySettings.mainVideoId"
              label="Select main video"
              item-text="title"
              item-value="id"
              autocomplete
              class="main-video-section__select"
              @input="onMainVideoSelect"
            />
          </v-flex>

          <v-flex>
            <content-editor
              v-model="displaySettings.eventTitle"
              label="Event title"
            />
          </v-flex>

          <v-flex>
            <content-editor
              label="Event description"
              v-model="displaySettings.eventDesc"
            />
          </v-flex>
        </v-layout>
      </v-container>
    </v-card>
    <!-- MAIN VIDEO -->

    <!-- GET VIDEOS ACCESS -->
    <v-card class="access-form-section">
      <h2>Videos access section</h2>
      <v-switch
        v-model="displaySettings.alwaysActive"
        label="Make videos always enabled (for auth and not auth users)"
      />
      <v-text-field
        v-model="displaySettings.accessFormTitle"
        label="Get videos access title"
      />
      <v-text-field
        v-model="displaySettings.accessFormSuccessMsg"
        label="Successful message"
      />
    </v-card>
    <!-- GET VIDEOS ACCESS -->

    <v-card class="display-settings-section">
      <!-- TABS -->
      <div>
        <h2 class="preview-title">Display preview</h2>
        <div class="display-settings-section__new-tab">
          <v-text-field
            v-model="newTabName"
            label="New tab name"
            class="display-settings-section__new-tab-name"
          />
          <v-btn
            color="success"
            :disabled="!newTabName || !isWriteAvailable"
            @click.native="onAddNewTab"
          >
            Add tab
          </v-btn>
        </div>
        <v-tabs v-if="tabs.length" fixed-tabs dark show-arrows centered>
          <v-tabs-slider color="red"></v-tabs-slider>
          <v-tab
            v-for="(tab, i) in tabs"
            :key="`tab-${i}`"
          >
            {{ tab.name }}
          </v-tab>
      <!-- TABS -->

          <!-- VIDEOS LIST -->
          <v-tab-item
            v-for="(tab, j) in tabs"
            :key="`videos-${j}`"
          >
            <v-card flat>
              <v-select
                :items="visibleVideos"
                v-model="displaySettings.tabs[j].videos"
                multiple
                label="Videos"
                item-text="title"
                item-value="id"
                autocomplete
                chips
                deletable-chips
                class="tab-video__select"
              />
          <!-- VIDEOS LIST -->

              <!-- VIDEOS PREVIEW -->
              <div>
                <drag :list="tab.videos" :options="dragOptions" class="videos">
                  <v-card
                    v-for="video in tab.videos"
                    :key="video.id"
                    class="video"
                  >
                    <div class="dragable-icon"><v-icon>swap_horiz</v-icon></div>
                    <div>
                      <a
                        :href="getVideoById(video).url"
                        target="_blank"
                      >
                        {{ getTextFromHTMLStr(getVideoById(video).title) }}
                      </a>
                    </div>
                  </v-card>
                </drag>
              </div>
              <!-- VIDEOS PREVIEW -->
            </v-card>
            <v-divider />
            <v-layout row align-center>
              <v-text-field
                v-model="tab.shareText"
                label="Share video text"
                class="share-video-text-input"
              />
              <v-spacer></v-spacer>
              <v-btn
                small
                color="error"
                @click="onTabDelete(j)"
                :disabled="!isWriteAvailable"
              >
                Delete tab
              </v-btn>
            </v-layout>
          </v-tab-item>
        </v-tabs>
      </div>
    </v-card>
  </div>
</template>

<script>
  import {mapActions, mapGetters} from 'vuex'
  import {clone, path, pathOr, prop} from 'ramda'
  import mixins from '@/utils/mixins'
  import getTextFromHTMLStr from '@/utils/getTextFromHTMLStr'

  export default {
    name: 'VideosDisplaySettings',
    mixins: [mixins.isWriteAvailable],
    data () {
      return {
        loaded: false,
        videosFetched: false,
        newTabName: '',
        displaySettings: {},
        dragOptions: {
          ghostClass: 'before-drag-drop'
        },
        // DON'T DELETE! need for isWriteAvailable mixin
        scopeForWrite: 'general_settings:write'
      }
    },
    watch: {
      getVideosDisplaySettings: {
        handler: function (val) {
          this.displaySettings = this.getDisplaySettings(val)
        },
        immediate: true
      }
    },
    computed: {
      ...mapGetters([
        'getVideos',
        'getVideoById',
        'getSettings'
      ]),
      getVideosDisplaySettings () {
        if (this.videosFetched) return path(['videoGallerySetting', 'metadataObject'], this.getSettings)
      },
      mainVideo () {
        return this.getVideoById(pathOr('', ['mainVideoId'], this.displaySettings))
      },
      tabs () {
        return pathOr([], ['tabs'], this.displaySettings)
      },
      visibleVideos () {
        return this.getVideos.reduce((acc, video) => {
          if (video.isVisible) {
            const visibleVideos = clone(video)
            visibleVideos.title = getTextFromHTMLStr(visibleVideos.title)
            acc.push(visibleVideos)
          }
          return acc
        }, [])
      }
    },
    methods: {
      ...mapActions([
        'fetchGeneralSettings',
        'saveVideosDisplaySettings',
        'fetchVideos'
      ]),
      getTextFromHTMLStr,
      onMainVideoSelect (videoId) {
        this.$set(this.displaySettings, 'mainVideoId', videoId)
      },
      onAddNewTab () {
        if (!this.newTabName) return

        const newTab = {
          name: this.newTabName,
          videos: []
        }

        if (!this.tabs.length) {
          this.$set(this.displaySettings, 'tabs', [])
        }
        this.displaySettings.tabs = [...this.displaySettings.tabs, newTab]
        this.newTabName = ''
      },
      onTabDelete (deletableTabIndex) {
        this.displaySettings.tabs = this.displaySettings.tabs.filter((tab, i) => i !== deletableTabIndex)
      },
      async onSaveDisplaySettings () {
        await this.saveVideosDisplaySettings(this.displaySettings)
        this.fetchGlobalSettings()
      },
      async fetchGlobalSettings () {
        await this.fetchGeneralSettings()
      },
      // check if some video is deleted etc
      getDisplaySettings (source) {
        const result = {...source}
        if (result.alwaysActive === undefined) {
          result.alwaysActive = false
        }
        const mainVideoId = pathOr('', ['mainVideoId'], source)
        const tabs = pathOr([], ['tabs'], source)

        // check mainVideoId
        const videoWithMatchedId = this.getVideoById(mainVideoId)
        result.mainVideoId =  prop('isVisible', videoWithMatchedId)
          ? mainVideoId
          : ''

        // check videos in tabs
        const checkedTabs = tabs.map(tab => {
          const videos = pathOr([], ['videos'], tab)
          return {
            name: tab.name,
            videos: videos.filter(video => this.getVideoById(video) && this.getVideoById(video).isVisible),
            shareText: tab.shareText || ''
          }
        })
        result.tabs = [...checkedTabs]

        return result
      }
    },
    async created () {
      // IMPORTANT: fetchVideos before fetchVideosDisplaySettings
      try {
        await this.fetchVideos()
        this.videosFetched = true
      } catch (err) {
        throw err
      }
      if (!this.getSettings) {
        await this.fetchGlobalSettings()
      }
      // this.displaySettings = this.getDisplaySettings(this.getVideosDisplaySettings)
      this.loaded = true
    }
  }
</script>

<style lang="scss" scoped>
  .main-video-section {
    padding: 10px;
    margin-bottom: 10px;

    &__selected-title {
      display: inline-block;
      margin-bottom: 20px;
    }

    &__select {
      max-width: 300px;
    }
  }

  .access-form-section {
    padding: 10px;
    margin-bottom: 10px;
  }

  .preview-title {
    margin-left: 10px;
    padding-top: 10px;
  }

  .display-settings-section {
    &__new-tab {
      display: flex;
      padding: 0 10px;

      &-name {
        max-width: 300px;
      }
    }
  }

  .tab-video__select {
    width: calc(100% - 20px);
    margin: 10px;
  }

  .videos {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
  }

  .video {
    display: flex;
    width: calc(25% - 20px);
    padding: 10px;
    margin: 10px;
    cursor: grab;
  }

  .dragable-icon {
    margin-right: 10px;
  }

  .before-drag-drop {
    opacity: 0.35;
    background: lightgray;
  }

  .share-video-text-input {
    max-width: 300px;
    margin-left: 10px;
  }

  @media screen and (max-width: 768px) {
    .videos {
      flex-direction: column;
    }

    .video {
      width: calc(100% - 20px);
      margin: 10px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/Videos/Display/index.vue